import React from 'react';
import Avatar from '../atoms/Avatar';
import Badge from '../atoms/Badge';
import Button from '../atoms/Button';
import Icon from '../atoms/Icon';

const JobCard = ({
  job = {},
  onViewDetails,
  applicationStatus,
  className = ''
}) => {

  const handleViewDetails = (e) => {
    e.stopPropagation();
    onViewDetails?.();
  };
  
  const {
    title = 'Job Title',
    company = 'Company Name',
    location = 'Location',
    description = 'Job description',
    type = 'Full-time',
    workType = 'Remote',
    logo,
    isAIMatch = false,
    postedDate = '2 days ago',
    live_interview_enabled = false,
  } = job;
  
  return (
    <div 
      className={`group flex flex-col bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm hover:shadow-xl hover:border-blue-500 dark:hover:border-blue-500 transition-all duration-300 cursor-pointer h-full ${className}`}
      onClick={handleViewDetails}
    >
      {/* Company Logo and Badges */}
      <div className="flex items-start justify-between mb-4">
        {logo && (
          <div className="flex-shrink-0">
            <Avatar 
              src={logo} 
              alt={`${company} logo`} 
              size={14}
              className="rounded-lg bg-white p-2 border border-gray-200 dark:border-gray-700 shadow-sm"
            />
          </div>
        )}
        <div className="flex flex-col gap-2 items-end">
          {isAIMatch && (
            <div className="flex items-center gap-1.5 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-semibold px-3 py-1.5 rounded-full shadow-sm">
              <Icon name="auto_awesome" size={14} />
              <span>AI Match</span>
            </div>
          )}
          {live_interview_enabled && (
            <div className="flex items-center gap-1.5 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 text-xs font-semibold px-3 py-1.5 rounded-full border border-blue-200 dark:border-blue-800">
              <Icon name="record_voice_over" size={14} />
              <span>Live</span>
            </div>
          )}
          {applicationStatus && (() => {
            const configs = {
              rejected: { bg: 'bg-red-50 dark:bg-red-900/20', text: 'text-red-700 dark:text-red-300', border: 'border-red-200 dark:border-red-800', icon: 'cancel', label: 'Not Selected' },
              offer: { bg: 'bg-green-50 dark:bg-green-900/20', text: 'text-green-700 dark:text-green-300', border: 'border-green-200 dark:border-green-800', icon: 'check_circle', label: 'Selected' },
              interview_completed: { bg: 'bg-purple-50 dark:bg-purple-900/20', text: 'text-purple-700 dark:text-purple-300', border: 'border-purple-200 dark:border-purple-800', icon: 'hourglass_top', label: 'Under Review' },
              completed: { bg: 'bg-purple-50 dark:bg-purple-900/20', text: 'text-purple-700 dark:text-purple-300', border: 'border-purple-200 dark:border-purple-800', icon: 'hourglass_top', label: 'Under Review' },
              review: { bg: 'bg-yellow-50 dark:bg-yellow-900/20', text: 'text-yellow-700 dark:text-yellow-300', border: 'border-yellow-200 dark:border-yellow-800', icon: 'visibility', label: 'Under Review' },
            };
            const c = configs[applicationStatus] || { bg: 'bg-gray-50 dark:bg-gray-900/20', text: 'text-gray-700 dark:text-gray-300', border: 'border-gray-200 dark:border-gray-800', icon: 'check', label: 'Applied' };
            return (
              <div className={`flex items-center gap-1.5 ${c.bg} ${c.text} text-xs font-semibold px-3 py-1.5 rounded-full border ${c.border}`}>
                <Icon name={c.icon} size={14} />
                <span>{c.label}</span>
              </div>
            );
          })()}
        </div>
      </div>
      
      {/* Job Title */}
      <h3 className="font-bold text-xl mb-2 text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors line-clamp-2">
        {title}
      </h3>
      
      {/* Company Name */}
      <p className="font-semibold text-gray-700 dark:text-gray-300 mb-1">
        {company}
      </p>
      
      {/* Location */}
      <div className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400 mb-4">
        <Icon name="location_on" size={16} />
        <span>{location}</span>
      </div>
      
      {/* Description */}
      <p className="text-gray-600 dark:text-gray-400 text-sm mb-4 flex-grow line-clamp-3 leading-relaxed">
        {description}
      </p>
      
      {/* Job Type Badges */}
      <div className="flex flex-wrap gap-2 mb-4">
        <Badge variant="default" className="bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800">
          {type}
        </Badge>
        <Badge variant="secondary" className="bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800">
          {workType}
        </Badge>
      </div>
      
      {/* Footer */}
      <div className="flex items-center justify-between mt-auto pt-4 border-t border-gray-100 dark:border-gray-700">
        <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
          <Icon name="schedule" size={14} />
          <span>Posted {postedDate}</span>
        </div>
        <Button 
          variant="outline" 
          size="sm"
          onClick={handleViewDetails}
          className="group-hover:bg-blue-600 group-hover:text-white group-hover:border-blue-600 transition-colors"
        >
          View Details
        </Button>
      </div>
    </div>
  );
};

export default JobCard;

